import React, { useEffect, useRef, useState } from 'react';

import { type CardDispute, type CardDisputeDocumentEvidenceInput, CardDisputeNextAction } from '@column/api';
import { Button } from '@column/column-ui-kit';
import { Alert, AlertDescription, AlertTitle, Textarea } from '@column/ui';

import { useAddCardDisputeEvidence } from '~/lib/api/cardDisputes';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { DisputeEvidenceComposer } from './DisputeEvidenceComposer';
import { getEvidenceErrorMessage } from './evidence';

export const AddDisputeEvidence: React.FC<{ dispute: CardDispute }> = ({ dispute }) => {
  const [documents, setDocuments] = useState<CardDisputeDocumentEvidenceInput[]>([]);
  const [narrative, setNarrative] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string>();

  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const contextKey = `${dispute.id}:${platformId ?? ''}:${isSandbox ? 'sandbox' : 'live'}`;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;
  const previousContextKeyRef = useRef(contextKey);

  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const addEvidenceMutation = useAddCardDisputeEvidence({
    disputeId: dispute.id,
    transactionId: dispute.card_transaction_id,
  });

  useEffect(() => {
    if (previousContextKeyRef.current === contextKey) {
      return;
    }
    previousContextKeyRef.current = contextKey;
    setDocuments([]);
    setNarrative('');
    setUploadBusy(false);
    setAttaching(false);
    setAttachmentError(undefined);
  }, [contextKey]);

  useEffect(() => {
    contextKeyRef.current = contextKey;
    return () => {
      contextKeyRef.current = '';
    };
  }, [contextKey]);

  const pendingNetworkSubmission =
    dispute.submission_status === 'pending_submission' ||
    dispute.next_actions.includes(CardDisputeNextAction.AWAIT_NETWORK_SUBMISSION);
  const disabled = pendingNetworkSubmission || attaching;
  const hasEvidence = documents.length > 0 || narrative.trim().length > 0;

  const handleAttach = async () => {
    if (!hasEvidence || disabled || uploadBusy) {
      return;
    }

    const mutationContextKey = contextKeyRef.current;
    const failures: string[] = [];
    let successCount = 0;
    setAttaching(true);
    setAttachmentError(undefined);

    for (const document of documents) {
      if (contextKeyRef.current !== mutationContextKey) {
        break;
      }
      try {
        await addEvidenceMutation.mutateAsync({ evidence: document });
        successCount += 1;
        if (contextKeyRef.current === mutationContextKey) {
          setDocuments((current) => current.filter((entry) => entry.document_id !== document.document_id));
        }
      } catch (error) {
        failures.push(getEvidenceErrorMessage(error, `Failed to attach document ${document.document_id}.`));
      }
    }

    const trimmedNarrative = narrative.trim();
    if (trimmedNarrative && contextKeyRef.current === mutationContextKey) {
      try {
        await addEvidenceMutation.mutateAsync({ evidence: { narrative: trimmedNarrative } });
        successCount += 1;
        if (contextKeyRef.current === mutationContextKey) {
          setNarrative('');
        }
      } catch (error) {
        failures.push(getEvidenceErrorMessage(error, 'Failed to attach narrative evidence.'));
      }
    }

    if (contextKeyRef.current === mutationContextKey) {
      setAttaching(false);
      if (failures.length > 0) {
        const message = failures[0];
        setAttachmentError(message);
        addDangerNotification({ content: message });
      } else if (successCount > 0) {
        addSuccessNotification({ content: 'Evidence attached to dispute' });
      }
    }
  };

  return (
    <div className="flex flex-col gap-4" data-dd-privacy="hidden">
      {pendingNetworkSubmission && (
        <Alert>
          <AlertTitle>Submission in progress</AlertTitle>
          <AlertDescription>Evidence attachments are disabled until the current submission finishes.</AlertDescription>
        </Alert>
      )}

      {dispute.submission_status !== 'draft' && !pendingNetworkSubmission && (
        <Alert>
          <AlertTitle>Attachment is not network submission</AlertTitle>
          <AlertDescription>
            New evidence is attached to this dispute in Column. It is sent to the Card Network only when included in a
            supported response.
          </AlertDescription>
        </Alert>
      )}

      <DisputeEvidenceComposer
        key={contextKey}
        value={documents}
        onChange={setDocuments}
        disabled={disabled}
        onBusyChange={setUploadBusy}
      />

      <div>
        <label className="text-sm font-medium text-foreground" htmlFor="card-dispute-evidence-narrative">
          Narrative evidence
        </label>
        <Textarea
          id="card-dispute-evidence-narrative"
          className="mt-2"
          value={narrative}
          disabled={disabled}
          placeholder="Add information that supports the dispute"
          data-dd-privacy="hidden"
          onChange={(event) => setNarrative(event.target.value)}
        />
      </div>

      {attachmentError && (
        <Alert variant="destructive">
          <AlertTitle>Evidence was not fully attached</AlertTitle>
          <AlertDescription>
            {attachmentError} Uploaded documents remain available to retry in this session.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          size="small"
          isLoading={attaching}
          isDisabled={disabled || uploadBusy || !hasEvidence}
          onClick={handleAttach}
        >
          Attach evidence
        </Button>
      </div>
    </div>
  );
};
